/**
 * Utility functions for transforming data in pumpage reports
 * Handles null, empty, and array field transformations consistently
 */

/**
 * Transform array fields for display and searchability
 * @param {any} value - The array value to transform
 * @returns {string} - Transformed display value
 */
export const transformArrayField = (value) => {
  // Handle null/undefined values - for arrays, null means "empty array"
  if (value === null || value === undefined) {
    return "<empty>";
  }

  // Handle array values
  if (Array.isArray(value)) {
    // Empty array
    if (value.length === 0) {
      return "<empty>";
    }

    // Array with single null element (PostgreSQL {null})
    if (value.length === 1 && value[0] === null) {
      return "<empty>";
    }

    // Filter out null values and join
    const validValues = value.filter((v) => v !== null && v !== undefined);
    if (validValues.length === 0) {
      return "<empty>";
    }

    return validValues.join(", ");
  }

  // Not an array, return as-is
  return value;
};

/**
 * Transform text fields for display and searchability
 * @param {any} value - The text value to transform
 * @returns {string} - Transformed display value
 */
export const transformTextField = (value) => {
  // Handle null, undefined, empty string, or whitespace-only
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return "<blank>";
  }

  return value;
};

/**
 * Check if a value represents a null/empty state for lookup generation
 * @param {any} value - The value to check
 * @returns {boolean} - True if value represents null/empty state
 */
export const isNullOrEmpty = (value) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string" && value.trim() === "") return true;

  // For arrays, we don't consider any array state as "null" since null arrays become "<empty>"
  // This function is for text fields that become "<blank>"
  return false;
};

/**
 * Check if a value represents an empty array state for lookup generation
 * @param {any} value - The value to check
 * @returns {boolean} - True if value represents empty array state
 */
export const isEmptyArray = (value) => {
  return (
    Array.isArray(value) &&
    (value.length === 0 || (value.length === 1 && value[0] === null))
  ); // {null} is also empty
};

/**
 * Generate lookup options from raw data for a text field
 * @param {Array} data - Raw data array
 * @param {string} fieldName - Name of the field to extract values from
 * @returns {Array} - Sorted array of unique values including <blank> if needed
 */
export const generateTextFieldLookup = (data, fieldName) => {
  const valueSet = new Set();
  let hasNull = false;

  for (const row of data) {
    const value = row[fieldName];
    if (isNullOrEmpty(value)) {
      hasNull = true;
    } else if (value && value.trim()) {
      valueSet.add(value.trim());
    }
  }

  const sortedValues = Array.from(valueSet).sort((a, b) =>
    String(a).localeCompare(String(b))
  );

  if (hasNull) {
    sortedValues.push("<blank>");
  }

  return sortedValues;
};

/**
 * Generate lookup options from raw data for an array field
 * @param {Array} data - Raw data array
 * @param {string} fieldName - Name of the field to extract values from
 * @returns {Array} - Sorted array of unique values including <blank>/<empty> if needed
 */
export const generateArrayFieldLookup = (data, fieldName) => {
  const valueSet = new Set();
  let hasEmpty = false;

  for (const row of data) {
    const value = row[fieldName];

    // All "empty" array states (null, [], {null}) should be treated as <empty>
    if (isEmptyArray(value) || value === null || value === undefined) {
      hasEmpty = true;
    } else if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined && item.trim && item.trim()) {
          valueSet.add(item.trim());
        }
      });
    }
  }

  const sortedValues = Array.from(valueSet).sort((a, b) =>
    String(a).localeCompare(String(b))
  );

  // Only add <empty> for arrays (no <blank> since arrays don't use <blank>)
  if (hasEmpty) {
    sortedValues.push("<empty>");
  }

  return sortedValues;
};
